import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import PageHelp from '../../components/PageHelp'

interface Account {
  id: string
  code: string
  name: string
  accountType: string
  rootType: string
  parentId?: string
  isGroup: boolean
  isActive: boolean
  currency: string
  balance: string
  isBankAccount?: boolean
  allowReconciliation?: boolean
  parent?: Account
  children?: Account[]
}

const ChartOfAccounts: React.FC = () => {
  // const navigate = useNavigate() // Reserved for future use
  const { user } = useAuthStore()
  const { hasPermission } = usePermissions(user)
  const [accounts, setAccounts] = useState<Account[]>([])
  const [accountTree, setAccountTree] = useState<Account[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<'list' | 'tree'>('tree')
  const [selectedRootType, setSelectedRootType] = useState<string>('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingAccount, setEditingAccount] = useState<Account | null>(null)
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    accountType: 'ASSET',
    rootType: 'ASSET',
    parentId: '',
    isGroup: false,
    currency: 'GBP',
    openingBalance: '0',
    openingBalanceDate: '',
    isBankAccount: false,
    allowReconciliation: false,
  })

  useEffect(() => {
    fetchAccounts()
  }, [selectedRootType, viewMode])

  const fetchAccounts = async () => {
    try {
      setIsLoading(true)
      setError(null)

      if (viewMode === 'tree') {
        const response = await axios.get('/api/erp/accounts/tree', {
          params: selectedRootType ? { rootType: selectedRootType } : {},
        })
        setAccountTree(response.data.data || [])
      } else {
        const response = await axios.get('/api/erp/accounts', {
          params: selectedRootType ? { rootType: selectedRootType } : {},
        })
        setAccounts(response.data.data || [])
      }
    } catch (error: any) {
      console.error('Error fetching accounts:', error)
      setError(error.response?.data?.error || 'Failed to load accounts')
      toast.error('Failed to load accounts')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const payload = {
        ...formData,
        openingBalance: formData.openingBalance ? parseFloat(formData.openingBalance) : 0,
        openingBalanceDate: formData.openingBalanceDate || undefined,
        parentId: formData.parentId || undefined,
      }

      await axios.post('/api/erp/accounts', payload)
      toast.success('Account created successfully')
      setShowCreateModal(false)
      resetForm()
      fetchAccounts()
    } catch (error: any) {
      console.error('Error creating account:', error)
      toast.error(error.response?.data?.error || 'Failed to create account')
    }
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingAccount) return

    try {
      await axios.put(`/api/erp/accounts/${editingAccount.id}`, {
        name: formData.name,
        isActive: editingAccount.isActive,
        currency: formData.currency,
        isBankAccount: formData.isBankAccount,
        allowReconciliation: formData.allowReconciliation,
      })
      toast.success('Account updated successfully')
      setShowEditModal(false)
      setEditingAccount(null)
      resetForm()
      fetchAccounts()
    } catch (error: any) {
      console.error('Error updating account:', error)
      toast.error(error.response?.data?.error || 'Failed to update account')
    }
  }

  const handleDelete = async (accountId: string) => {
    if (!confirm('Are you sure you want to delete this account?')) return

    try {
      await axios.delete(`/api/erp/accounts/${accountId}`)
      toast.success('Account deleted successfully')
      fetchAccounts()
    } catch (error: any) {
      console.error('Error deleting account:', error)
      toast.error(error.response?.data?.error || 'Failed to delete account')
    }
  }

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      accountType: 'ASSET',
      rootType: 'ASSET',
      parentId: '',
      isGroup: false,
      currency: 'GBP',
      openingBalance: '0',
      openingBalanceDate: '',
      isBankAccount: false,
      allowReconciliation: false,
    })
  }

  const openEditModal = (account: Account) => {
    setEditingAccount(account)
    setFormData({
      code: account.code,
      name: account.name,
      accountType: account.accountType,
      rootType: account.rootType,
      parentId: account.parentId || '',
      isGroup: account.isGroup,
      currency: account.currency,
      openingBalance: '0',
      openingBalanceDate: '',
      isBankAccount: account.isBankAccount || false,
      allowReconciliation: account.allowReconciliation || false,
    })
    setShowEditModal(true)
  }

  const renderAccountTree = (accounts: Account[], level: number = 0): JSX.Element[] => {
    return accounts.map((account) => (
      <React.Fragment key={account.id}>
        <tr className={account.isGroup ? 'bg-gray-50 font-semibold hover:bg-gray-100' : 'hover:bg-gray-50'}>
          <td className="px-8 py-5 border-b" style={{ paddingLeft: `${level * 40 + 32}px` }}>
            <div className="flex items-center space-x-3">
              {level > 0 && (
                <span className="text-gray-300 text-lg">└</span>
              )}
              {account.isGroup && (
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              )}
              <span className={account.isGroup ? 'font-semibold text-gray-900' : 'text-gray-700'}>{account.code}</span>
            </div>
          </td>
          <td className="px-8 py-5 border-b">
            <span className={account.isGroup ? 'font-semibold text-gray-900' : 'text-gray-700'}>{account.name}</span>
          </td>
          <td className="px-8 py-5 border-b text-gray-600">{account.rootType}</td>
          <td className="px-8 py-5 border-b text-gray-600">{account.accountType}</td>
          <td className="px-8 py-5 border-b text-right font-medium">
            {parseFloat(account.balance).toLocaleString('en-GB', {
              style: 'currency',
              currency: account.currency,
            })}
          </td>
          <td className="px-8 py-5 border-b">
            <span className={`px-2 py-1 text-xs rounded-full ${account.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {account.isActive ? 'Active' : 'Inactive'}
            </span>
          </td>
          <td className="px-8 py-5 border-b">
            <div className="flex space-x-3">
              {hasPermission('erp', 'write') && (
                <button
                  onClick={() => openEditModal(account)}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  Edit
                </button>
              )}
              {hasPermission('erp', 'admin') && !account.isGroup && (
                <button
                  onClick={() => handleDelete(account.id)}
                  className="text-red-600 hover:text-red-800 text-sm font-medium"
                >
                  Delete
                </button>
              )}
            </div>
          </td>
        </tr>
        {account.children && account.children.length > 0 && renderAccountTree(account.children, level + 1)}
      </React.Fragment>
    ))
  }

  if (!hasPermission('erp', 'read')) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">You don't have permission to view the Chart of Accounts.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <div className="flex items-center">
            <h1 className="text-2xl font-bold text-gray-900">Chart of Accounts</h1>
            <PageHelp pageIdentifier="chart-of-accounts" title="Chart of Accounts" />
          </div>
          <p className="text-gray-600 mt-1">Manage your accounting accounts</p>
        </div>
        {hasPermission('erp', 'write') && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Create Account</span>
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex items-center space-x-4">
          <div className="min-w-[140px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">View Mode</label>
            <select
              value={viewMode}
              onChange={(e) => {
                setViewMode(e.target.value as 'list' | 'tree')
                fetchAccounts()
              }}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="tree">Tree View</option>
              <option value="list">List View</option>
            </select>
          </div>
          <div className="min-w-[160px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">Account Type</label>
            <select
              value={selectedRootType}
              onChange={(e) => setSelectedRootType(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">All Types</option>
              <option value="ASSET">Assets</option>
              <option value="LIABILITY">Liabilities</option>
              <option value="EQUITY">Equity</option>
              <option value="INCOME">Income</option>
              <option value="EXPENSE">Expenses</option>
            </select>
          </div>
        </div>
      </div>

      {/* Accounts Table */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading accounts...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-8 py-5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                <th className="px-8 py-5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-8 py-5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Root Type</th>
                <th className="px-8 py-5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account Type</th>
                <th className="px-8 py-5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                <th className="px-8 py-5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-8 py-5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {viewMode === 'tree' && accountTree.length > 0 && renderAccountTree(accountTree)}
              {viewMode === 'list' && accounts.length > 0 && accounts.map((account) => (
                <tr key={account.id} className="hover:bg-gray-50">
                  <td className="px-8 py-5 border-b">
                    <span className="font-mono text-gray-900 font-medium">{account.code}</span>
                  </td>
                  <td className="px-8 py-5 border-b">
                    <span className="text-gray-700">{account.name}</span>
                  </td>
                  <td className="px-8 py-5 border-b text-gray-600">{account.rootType}</td>
                  <td className="px-8 py-5 border-b text-gray-600">{account.accountType}</td>
                  <td className="px-8 py-5 border-b text-right font-medium">
                    {parseFloat(account.balance).toLocaleString('en-GB', {
                      style: 'currency',
                      currency: account.currency,
                    })}
                  </td>
                  <td className="px-8 py-5 border-b">
                    <span className={`px-2 py-1 text-xs rounded-full ${account.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {account.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-8 py-5 border-b">
                    <div className="flex space-x-3">
                      {hasPermission('erp', 'write') && (
                        <button
                          onClick={() => openEditModal(account)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Edit
                        </button>
                      )}
                      {hasPermission('erp', 'admin') && !account.isGroup && (
                        <button
                          onClick={() => handleDelete(account.id)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {((viewMode === 'tree' && accountTree.length === 0) || (viewMode === 'list' && accounts.length === 0)) && (
            <div className="text-center py-12 text-gray-500">
              <p className="mb-2">No accounts found. Create your first account to get started.</p>
              {viewMode === 'tree' && (
                <p className="text-sm text-gray-400 mt-2">
                  Tip: Tree view shows hierarchical structure. Create group accounts (parent accounts) and assign child accounts to see the tree structure.
                </p>
              )}
            </div>
          )}
          {viewMode === 'tree' && accountTree.length > 0 && accountTree.every(acc => !acc.children || acc.children.length === 0) && (
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 m-4">
              <div className="flex">
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    <strong>Tree View Tip:</strong> All your accounts are at the root level. To see a hierarchical tree structure, create group accounts (parent accounts) and assign child accounts to them. The tree view will then show indentation and parent-child relationships.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Create Account</h2>
            <form onSubmit={handleCreate}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Account Code *</label>
                  <input
                    type="text"
                    required
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    placeholder="e.g., 1000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Account Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    placeholder="e.g., Cash"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Root Type *</label>
                    <select
                      required
                      value={formData.rootType}
                      onChange={(e) => setFormData({ ...formData, rootType: e.target.value, accountType: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    >
                      <option value="ASSET">Asset</option>
                      <option value="LIABILITY">Liability</option>
                      <option value="EQUITY">Equity</option>
                      <option value="INCOME">Income</option>
                      <option value="EXPENSE">Expense</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                    <select
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    >
                      <option value="GBP">GBP</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.isGroup}
                      onChange={(e) => setFormData({ ...formData, isGroup: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">Group Account (Parent)</span>
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Opening Balance</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.openingBalance}
                    onChange={(e) => setFormData({ ...formData, openingBalance: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Opening Balance Date</label>
                  <input
                    type="date"
                    value={formData.openingBalanceDate}
                    onChange={(e) => setFormData({ ...formData, openingBalanceDate: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.isBankAccount}
                      onChange={(e) => setFormData({ ...formData, isBankAccount: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">Bank Account</span>
                  </label>
                </div>
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.allowReconciliation}
                      onChange={(e) => setFormData({ ...formData, allowReconciliation: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">Allow Reconciliation</span>
                  </label>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false)
                    resetForm()
                  }}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Create Account
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Edit Account</h2>
            <form onSubmit={handleUpdate}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Account Code</label>
                  <input
                    type="text"
                    value={formData.code}
                    disabled
                    className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100"
                  />
                  <p className="text-xs text-gray-500 mt-1">Account code cannot be changed</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Account Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  >
                    <option value="GBP">GBP</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.isBankAccount}
                      onChange={(e) => setFormData({ ...formData, isBankAccount: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">Bank Account</span>
                  </label>
                </div>
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.allowReconciliation}
                      onChange={(e) => setFormData({ ...formData, allowReconciliation: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">Allow Reconciliation</span>
                  </label>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false)
                    setEditingAccount(null)
                    resetForm()
                  }}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Update Account
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default ChartOfAccounts
